import { Component, Input, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { CellRenderer, CellRendererParams } from '../../core/interfaces/cell-renderer.interface';

/**
 * Date Only Cell Renderer Component
 *
 * Renders dates in DD/MM/YYYY format without time.
 *
 * Features:
 * - Formats dates as DD/MM/YYYY
 * - Falls back to "-" for null/undefined values
 * - Handles invalid dates gracefully
 *
 * Usage in ColDef:
 * ```typescript
 * {
 *   field: 'creationDate',
 *   headerName: 'Fecha Creación',
 *   cellRenderer: DateOnlyCellRendererComponent,
 *   sortable: true
 * }
 * ```
 *
 * Output examples:
 * - "15/03/2024"
 * - "01/12/2023"
 */
@Component({
  selector: 'app-date-only-cell-renderer',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="text-content">
      {{ formattedDate }}
    </div>
  `,
  styles: [`
    :host {
      display: block;
      width: 100%;
    }
  `],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DateOnlyCellRendererComponent implements CellRenderer<any, Date | string> {
  /**
   * Parameters from the grid containing the cell value and row data
   */
  @Input() params!: CellRendererParams<any, Date | string>;

  /**
   * Get formatted date string
   */
  get formattedDate(): string {
    const value = this.params?.value;
    
    if (!value) return '-';
    
    const date = new Date(value);
    
    if (isNaN(date.getTime())) return '-';
    
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    
    return `${day}/${month}/${year}`;
  }
}
